import { removeStoredContactField, setLabelsToAddOnNextSubmit,
	resetLabelsToAddOnNextSubmit, getChats } from './storage';
import {createWidgetContainerNavigationComps} from './widget-container-navigation-comps';
import {createWidgetNavigationHandler, WidgetNavigator, NavigationState} from '../common/navigator';
import {Logger} from '../common/logger/logger';
import {categoriesToCategoryMap, CategoryMap, WidgetResolvedTicketSettings,
	CompleteCategory, updateFontStyleTag, classNames} from '../common/utils';
import { Article, WidgetAppearanceSettings, WidgetKbModuleSettings,
	WidgetAdvancedSettings, WidgetModuleType, WidgetChatSettings, createAxiosHttpAdapter, addDefaultHeaders } from '@wix/answers-api';
import { History } from 'history';
import * as React from 'react';
import { IndexRoute, Route, Router } from 'react-router';
import {EventType} from '../common/logger/event-type';
import { create } from '@wix/fedops-logger';
import { UnsubscribeFn } from '@wix/answers-app-core';
import { ChatTimelineMessage } from './new-widget-container/chat-room/chat-timeline/types';
import { WidgetMessageType, WidgetMessage } from '../common/message-types';
import { Icon, close } from '@wix/answers-ui-libby';
import { getIdealTextColor, TranslateFn } from '@wix/answers-lib';
import { ChatApi, createChatApi, ChatUserTimeline, WidgetKbApi, createWidgetKBApi } from './api';
import axios from 'axios';
import { AUTH_TOKEN_NAME } from './api/index';

const TOKEN_TTL = (5 * 60 * 1000) - (10 * 1000); // 5 minutes minus 10 second buffer
const EMBED_MESSAGE_TIMEOUT = 1000;

export interface SlimTenant {
	id: string;
	primaryLocale: string;
	timeZone: string;
	name: string;
}

export type WidgetConfig = {
	tenant: SlimTenant,
	appearance: WidgetAppearanceSettings,
	modulesConfig: ModulesConfig,
	kbModuleSettings: WidgetKbModuleSettings,
	ticketModuleSettings: WidgetResolvedTicketSettings,
	chatModuleSettings: WidgetChatSettings,
	advancedSettings: WidgetAdvancedSettings,
	publicReCaptchaKey: string
};

export type ModulesConfig = {
	defaultModule: WidgetModuleType,
	knowledgebaseEnabled: boolean,
	ticketsEnabled: boolean,
	chatEnabled: boolean
};

export type CustomHandlerGetterSetter = {
	setCustomHomePageHandler: (cb: ((html: string) => void)) => void;
	setCustomHeaderHandler: (cb: ((html: string) => void)) => void;
	setCustomHeroHtmlHandler: (cb: ((html: string) => void)) => void;
	setCustomFieldsHandler: (cb: ((customFields: {[key: string]: string}) => void)) => void;
	setIdentifyUserHandler: (cb: (email: string) => void) => void;
	setGoToArticleHandler: (cb: (articleId: string) => void) => void;
	setSendChatMessageHandler: (cb: (message: {content: string}) => void) => void;
	setOnChatStartedHandler: (cb: ((callback: (ticketId: string) => void) => void)) => void;
};

export interface WidgetContainerProps {
	locale: string;
	apiUrl: string;
	categoryTree: CompleteCategory[];
	baseUrl: string;
	widgetId: string;
	history: History;
	homepageArticles: Article[];
	config: WidgetConfig;
	logger: Logger;
	translate: TranslateFn;
	isPreview: boolean;
	isMobile: boolean;
	customHandlerGetterSetter: CustomHandlerGetterSetter;
}

export type WidgetContainerState = {
	isVisible: boolean;
	customHeader?: string;
	customHomePage?: string;
	customHeroHtml?: string;
	userEmail?: string;
	userFirstName?: string;
	customFields?: {[key: string]: string};
	authenticatorAdded: boolean;
	onChatStartedCallbacks: Array<(ticketId: string) => void>;
	greetingMessage?: string;
};

export type MessageHistory = {
	isVisible: () => boolean;
	getCustomHeader: () => string | undefined;
	getCustomHomePage: () => string | undefined;
	getCustomHeroHtml: () => string | undefined;
	getUserEmail: () => string | undefined;
	getUserFirstName: () => string | undefined;
	getCustomFields: () => {[key: string]: string} | undefined;
	authenticatorAdded: () => boolean;
	getGreetingMessageOverride: () => string | undefined;
};

export type SubscribeToUserTimeline = (
	ticketId: string,
	handler: (payload: ChatTimelineMessage) => void,
	fallbackHandler?: (timeline: ChatUserTimeline) => void
) => Promise<UnsubscribeFn>;

export type BaseWidgetProps = {
	categoryTree: CompleteCategory[];
	apiUrl: string;
	locale: string;
	widgetId: string;
	widgetConfig: WidgetConfig;
	categoryMap: CategoryMap;
	logger: Logger;
	getFedopsLogger: any;
	translate: TranslateFn;
	widgetNavigator: WidgetNavigator;
	messageHistory: MessageHistory;
	isPreview: boolean;
	isMobile: boolean;
	chatApi: ChatApi;
	onChatStarted: (ticketId: string, userId: string) => void;
	kbApi: WidgetKbApi;
};

export class WidgetContainer extends React.Component<WidgetContainerProps, WidgetContainerState> {
	widgetNavigator: any = null;
	fedopsLogger: any = null;
	unsubscribeUserTimeline: Promise<UnsubscribeFn | void>;
	token: string;
	tokenFetchTime: number;
	isFetchingToken: boolean;

	state: WidgetContainerState = {
		isVisible: false,
		authenticatorAdded: false,
		onChatStartedCallbacks: []
	};

	messageHistoryHandler = {
		isVisible: () => this.state.isVisible,
		getCustomHeader: () => this.state.customHeader,
		getCustomHomePage: () => this.state.customHomePage,
		getCustomHeroHtml: () => this.state.customHeroHtml,
		getUserEmail: () => this.state.userEmail,
		getUserFirstName: () => this.state.userFirstName,
		getCustomFields: () => this.state.customFields,
		authenticatorAdded: () => this.state.authenticatorAdded,
		getGreetingMessageOverride: () => this.state.greetingMessage
	};

	shouldComponentUpdate () {
		return false;
	}

	getFedopsLogger = () => {
		return this.fedopsLogger ? this.fedopsLogger : {interactionStarted: () => null, interactionEnded: () => null};
	}

	componentDidMount () {
		if (window) {
			window.addEventListener('message', this.handleSettingsPostMessage.bind(this));
			if (window.parent) {
				window.parent.postMessage({ready: true}, '*');
			}
			removeStoredContactField();
			resetLabelsToAddOnNextSubmit();
		}

		this.props.logger.logEvent(EventType.LOAD, {module: this.props.config.modulesConfig.defaultModule});

		if (window && (window as any).fedops && (window as any).fedops.apps) {
			this.fedopsLogger = create('answers-widget');
			this.fedopsLogger.appLoaded();
		}

		this.props.customHandlerGetterSetter.setCustomHeaderHandler(this.setCustomHeaderHandler);
		this.props.customHandlerGetterSetter.setCustomHomePageHandler(this.setCustomHomePageHandler);
		this.props.customHandlerGetterSetter.setCustomHeroHtmlHandler(this.setCustomHeroHtmlHandler);
		this.props.customHandlerGetterSetter.setIdentifyUserHandler(this.identifyUserHandler);
		this.props.customHandlerGetterSetter.setCustomFieldsHandler(this.setCustomFieldsHandler);
		this.props.customHandlerGetterSetter.setGoToArticleHandler((articleId) => {
			this.widgetNavigator.goToState(NavigationState.article, articleId);
		});
		this.props.customHandlerGetterSetter.setSendChatMessageHandler(this.sendChatMessageHandler);
		this.props.customHandlerGetterSetter.setOnChatStartedHandler(this.onChatStartedHandler);

		if (window && window.parent) {
			window.parent.postMessage({__ansWidget: {type: WidgetMessageType.VIEW_LOADED, data: {}}}, '*');
		}
	}

	setCustomHeaderHandler = (customHeader: string) => {
		this.setState({customHeader});
		window.postMessage(
			{__ansWidget: {type: WidgetMessageType.SET_CUSTOM_KB_HEADER, data: {headerHtml: customHeader}}
		}, '*');
	}

	setCustomHomePageHandler = (customHomePage: string) => {
		this.setState({customHomePage});
		window.postMessage(
			{__ansWidget: {type: WidgetMessageType.SET_CUSTOM_HOME_PAGE, data: {homePageHtml: customHomePage}}
		}, '*');
	}

	setCustomHeroHtmlHandler = (customHeroHtml: string) => {
		this.setState({customHeroHtml});
		window.postMessage(
			{__ansWidget: {type: WidgetMessageType.SET_CUSTOM_HERO_HTML, data: {heroHtml: customHeroHtml}}
		}, '*');
	}

	setCustomFieldsHandler = (customFields: {[key: string]: string}) => {
		this.setState({customFields});
		window.postMessage(
			{__ansWidget: {type: WidgetMessageType.SET_CUSTOM_FIELDS, data: {customFields}}
		}, '*');
	}

	identifyUserHandler = (userEmail: string, userFirstName?: string) => {
		this.setState({userEmail, userFirstName});
		window.postMessage(
			{__ansWidget: {type: WidgetMessageType.IDENTIFY_USER, data: {userData: {email: userEmail, name: userFirstName}}}
		}, '*');
	}

	sendChatMessageHandler = (message: {content: string}) => {
		window.postMessage(
			{__ansWidget: {type: WidgetMessageType.SEND_CHAT_MESSAGE, data: message}
		}, '*');
	}

	onChatStartedHandler = (callback: (ticketId: string) => void) => {
		this.setState({onChatStartedCallbacks: [...this.state.onChatStartedCallbacks, callback]});
	}

	componentWillUnmount () {
		if (window) {
			window.removeEventListener('message', this.handleSettingsPostMessage.bind(this));
		}
		if (this.unsubscribeUserTimeline) {
			this.unsubscribeUserTimeline.then((unsub) => unsub && unsub());
		}
	}

	handleSettingsPostMessage (event: any) {
		const isVisible: any = event.data.isVisible;
		if (typeof(isVisible) === 'boolean') {
			const openMethod = event.data.isTrusted ? 'click' : 'api';
			const moduleName = WidgetModuleType[this.widgetNavigator.getCurrModules()];
			if (isVisible) {
				this.props.logger.logEvent(EventType.OPEN, {openMethod, moduleName});
			} else {
				this.props.logger.logEvent(EventType.CLOSE, {openMethod, moduleName});
			}
			this.props.logger.setVisibility(isVisible);
			this.setState({isVisible});
		}

		const firstOpen: any = event.data.firstOpen;
		if (firstOpen) {
			this.props.logger.logEvent(EventType.FIRST_OPEN, {module: this.props.config.modulesConfig.defaultModule});
		}

		const newSettings: any = event.data.previewNewSettings;
		if (newSettings) {
			const font: any = newSettings.font;
			if (font && font.url && font.name) {
				updateFontStyleTag(font.url, font.name);
			}
		}

		const transition = event.data.previewOverwriteTransition;
		if (transition) {
			this.widgetNavigator.setOverwriteTransition(transition);
		}

		const goToKbHome: any = event.data.goToKbHome;
		if (goToKbHome) {
			this.widgetNavigator.goToState(NavigationState.kb, {});
		}

		const goToChat: any = event.data.goToChat;
		if (goToChat) {
			this.widgetNavigator.goToState(NavigationState.chat, {});
		}

		const labelsOnSubmit: any = event.data.labelsOnSubmit;
		if (labelsOnSubmit) {
			setLabelsToAddOnNextSubmit(labelsOnSubmit);
		}

		const goToArticleId: any = event.data.goToArticleId;
		if (goToArticleId) {
			this.widgetNavigator.goToState(NavigationState.article, goToArticleId);
		}

		const goToCategoryId: any = event.data.goToCategoryId;
		if (goToCategoryId) {
			this.widgetNavigator.goToState(NavigationState.category, goToCategoryId);
		}

		const goToContact: any = event.data.goToContact;
		if (goToContact) {
			this.widgetNavigator.goToState(NavigationState.tickets, {});
		}

		const switchModules: any = event.data.switchModules;
		if (switchModules === 'kb') {
			this.widgetNavigator.switchModules(WidgetModuleType.KB);
		} else if (switchModules === 'tickets') {
			this.widgetNavigator.switchModules(WidgetModuleType.TICKET);
		} else if (switchModules === 'chat') {
			this.widgetNavigator.switchModules(WidgetModuleType.CHAT);
		}

		const widgetMessage: WidgetMessage = event.data.__ansWidget;
		if (widgetMessage) {
			this.handleWidgetMessage(widgetMessage);
		}
	}

	handleWidgetMessage = (message: WidgetMessage) => {
		switch (message.type) {
			case WidgetMessageType.SET_CUSTOM_KB_HEADER:
				this.setState({customHeader: message.data.headerHtml});
				break;
			case WidgetMessageType.SET_CUSTOM_HOME_PAGE:
				this.setState({customHomePage: message.data.homePageHtml});
				break;
			case WidgetMessageType.SET_CUSTOM_HERO_HTML:
				this.setState({customHeroHtml: message.data.heroHtml});
				break;
			case WidgetMessageType.IDENTIFY_USER:
				const {email: userEmail, name: userFirstName} = message.data.userData;
				this.setState({userEmail, userFirstName});
				break;
			case WidgetMessageType.SET_CUSTOM_FIELDS:
				this.setState({customFields: message.data.customFields});
				break;
			case WidgetMessageType.AUTHENTICATOR_ADDED:
				this.setState({authenticatorAdded: true});
				break;
			case WidgetMessageType.OVERRIDE_GREETING_MESSAGE:
				this.setState({greetingMessage: message.data.greetingMessage});
				break;
			default:
				break;
		}
	}

	onReceiveMessage = () => {
		parent.postMessage({__ansWidget: {type: WidgetMessageType.CHAT_MESSAGE_RECEIVED, data: {}}}, '*');
	}

	onClose = () => {
		parent.postMessage({__ansWidget: {type: WidgetMessageType.CLOSE_WIDGET, data: {}}}, '*');
	}

	getAuthToken = () => {
		if (!this.state.authenticatorAdded) {
			return Promise.resolve();
		}
		if (this.token && this.tokenFetchTime > Date.now() - TOKEN_TTL) {
			return Promise.resolve(this.token);
		} else {
			return new Promise<string | void>((res) => {
				const timer = setTimeout(() => {
					window.removeEventListener('message', handleTokenResponseMessage);
					// tslint:disable-next-line:no-console
					console.log('No response for token request');
					res();
				}, 15000);
				const handleTokenResponseMessage = (event: MessageEvent) => {
					const widgetMessage: WidgetMessage = event.data.__ansWidget;
					if (widgetMessage && widgetMessage.type === WidgetMessageType.AUTHENTICATION_RESPONSE) {
						clearTimeout(timer);
						this.tokenFetchTime = Date.now();
						this.token = widgetMessage.data.token;
						window.removeEventListener('message', handleTokenResponseMessage);
						res(this.token);
					}
				};
				window.addEventListener('message', handleTokenResponseMessage);
				if (!this.isFetchingToken) {
					this.isFetchingToken = true;
					parent.postMessage({__ansWidget: {type: WidgetMessageType.AUTHENTICATION_REQUEST, data: {}}}, '*');
				}
			});
		}
	}

	getAuthHeaders = async () => {
		const token = await this.getAuthToken();
		return token ? {[AUTH_TOKEN_NAME]: token} : {};
	}

	render () {
		const history = this.props.history;
		const config = this.props.config;
		const categoryMap = categoriesToCategoryMap(this.props.categoryTree);
		this.widgetNavigator = createWidgetNavigationHandler(history as any);

		const tenantId = config.tenant.id;
		const tenantName = config.tenant.name;

		const subscribeToChat = (ticketId: string, userId: string) => {
			this.unsubscribeUserTimeline = baseWidgetProps.chatApi.subscribeToUserTimeline(ticketId,
				this.onReceiveMessage).then((unsub) => {
				baseWidgetProps.chatApi.presence.setOnline(ticketId, userId, window, document);
				return unsub;
			}).catch((error) => {
				// tslint:disable-next-line:no-console
				console.log(`Could not subscribe to real time updates: ${error}`);
			});
		};

		const onChatStarted = (ticketId: string, userId: string) => {
			this.state.onChatStartedCallbacks.forEach((cb) => cb(ticketId));
			subscribeToChat(ticketId, userId);
		};

		const wrappedAxios = addDefaultHeaders(createAxiosHttpAdapter(axios), this.getAuthHeaders);
		const {apiUrl, locale, widgetId} = this.props;

		const baseWidgetProps: BaseWidgetProps = {
			categoryTree: this.props.categoryTree,
			categoryMap,
			apiUrl: this.props.apiUrl,
			widgetId: this.props.widgetId,
			locale: this.props.locale,
			logger: this.props.logger,
			getFedopsLogger: this.getFedopsLogger,
			translate: this.props.translate,
			widgetConfig: config,
			widgetNavigator: this.widgetNavigator,
			messageHistory: this.messageHistoryHandler,
			isPreview: this.props.isPreview,
			isMobile: this.props.isMobile,
			chatApi: createChatApi(this.props.apiUrl, tenantName, tenantId, this.props.logger, this.getAuthToken),
			onChatStarted,
			kbApi: createWidgetKBApi(wrappedAxios, apiUrl, locale, widgetId)
		};

		const savedChats = getChats(this.props.widgetId);
		if (savedChats.length) {
			const {ticketId, userId} = savedChats[0];

			// to allow time for authenticator-added post message to arrive from embed
			setTimeout(() => subscribeToChat(ticketId, userId), EMBED_MESSAGE_TIMEOUT);
		}

		const widgetContainerNavigationComps = createWidgetContainerNavigationComps(
			baseWidgetProps,
			this.props.homepageArticles
		);

		const closeIconColor = getIdealTextColor(this.props.config.appearance.brandColor);
		const closeIcon = (
			<div className='mobile-close-icon' onClick={this.onClose} style={{color: closeIconColor}}>
				<Icon icon={close} />
			</div>
		);

		return (
			<div className={classNames('router-container', {mobile: this.props.isMobile})}>
				{this.props.isMobile ? closeIcon : null}
				<Router history={history}>
					<Route path='/' component={widgetContainerNavigationComps.DefaultModuleRedirectContainerComp} />
					<Route component={widgetContainerNavigationComps.ModuleContainer}>
						<Route path='/kb' component={widgetContainerNavigationComps.KbContainer}>
							<IndexRoute component={widgetContainerNavigationComps.KbHomepage} />
							<Route path='/search(/:term)' component={widgetContainerNavigationComps.SearchResults} />
							<Route
								path='/article/:articleId'
								component={widgetContainerNavigationComps.ArticlePage}
								onEnter={widgetContainerNavigationComps.onArticlePageEnter}
							/>
							<Route
								path='/article-uri/:articleUri'
								component={widgetContainerNavigationComps.ArticlePage}
								onEnter={widgetContainerNavigationComps.onArticleUriPageEnter}
							/>
							<Route path='/category/:categoryId' component={widgetContainerNavigationComps.CategoryPage} />
						</Route>
						<Route path='/tickets' component={widgetContainerNavigationComps.TicketsContainer}>
							<IndexRoute component={widgetContainerNavigationComps.ContactPage} />
							<Route path='/helped' component={widgetContainerNavigationComps.HalpedPage} />
						</Route>
						<Route path='/chat' component={widgetContainerNavigationComps.TicketsContainer}>
							<IndexRoute component={widgetContainerNavigationComps.ChatPage} />
						</Route>
					</Route>
					<Route path='/404' component={widgetContainerNavigationComps.NotFound} />
					<Route path='*' component={widgetContainerNavigationComps.LoaderComp} />
				</Router>
			</div>
		);
	}
}
